
import { Vue, Options } from "vue-class-component";
import { DepartmentApi, Department, ApplicantApi, SystemApi, Occupation, EmployeeApi } from "../../autogen/api";
import { PricePlanEnum, GetApplicantProfile, GetEmployeeProfile, ApplicantStatsEnum, GetRetiredProfile } from "../../autogen";
import { Emit, Prop, Watch } from "vue-property-decorator";
import UserSelector from "../molecules/UserSelector.vue";
import ActionButton from "../atoms/ActionButton.vue";
import { SelectableProfile } from "../../types";
import { ApplicantDepartmentId, RetiredDepartmentId } from "../../helpers/const";
import Store from "../../store";

@Options({
  components: { ActionButton, UserSelector }
})
export default class DepartmentUserSelector extends Vue {
  private readonly storeInstance = Store.getInstance();
  departments: Department[] = [];
  departmentUsers: { [key: string]: SelectableProfile[] } = {};
  occupationUsers: { [key: string]: SelectableProfile[] } = {};
  occupations: Occupation[] = [];
  currentDepartment: Department | null = null;
  currentOccupation: Occupation | null = null;
  @Prop({ type: Array }) initialSelectedUsers?: SelectableProfile[] = [];
  selectedUsers: SelectableProfile[] = [];
  currentCategory = "department";
  @Prop({ type: Boolean }) applicantOnly = false;
  @Prop({ type: Boolean }) includeRetired = false;
  @Prop({ type: Boolean, default: true }) includeApplicant = true;
  @Prop({ type: Boolean }) full = false;
  @Prop({ type: Boolean }) analysisFlg = false;
  @Prop({ type: Boolean, default: true }) isLimiting = true;
  @Prop({ type: Number }) participantsMax?: number = -1;
  @Prop({ type: Number }) participantsMin?: number = -1;
  processing = false;
  provisionalLimit = "10000";
  isReading = false;
  applicantStatus = [ApplicantStatsEnum.Screening, ApplicantStatsEnum.Hired, ApplicantStatsEnum.Joined].join(",");

  mounted() {
    this.loadInitialData();
  }
  async loadInitialData() {
    const deps = (this.storeInstance.getShortDepartmentsList({ withoutAll: false }) as unknown) as Department[];
    this.departments =
      this.includeApplicant && Store.getInstance().getAuth()!.pricePlan !== PricePlanEnum.Trial
        ? deps.concat({
            id: ApplicantDepartmentId,
            name: "応募者"
          })
        : deps;

    if (this.includeRetired) this.departments.push({ id: RetiredDepartmentId, name: "退職者" });
    const occRes = await new SystemApi().getOccupations({
      usedWithinClient: true,
      participantType: this.applicantOnly ? "applicant" : "employee"
    });
    this.occupations = occRes.data;
    this.selectedUsers = this.initialSelectedUsers || [];
  }
  @Watch("initialSelectedUsers")
  refresh() {
    this.selectedUsers = this.initialSelectedUsers || [];
  }
  /**
   * 部署idから従業員・応募者の一覧をセットする
   * @param {Department} department 部署オブジェクト
   * @return void
   */
  async onDepartmentSelect(department: Department) {
    this.isReading = true;
    this.currentDepartment = department;
    const departmentId = department.id;
    if (this.departmentUsers[departmentId]) return;
    let response;
    if (this.applicantOnly) {
      response = await new ApplicantApi().getApplicants({
        departmentId,
        status: this.applicantStatus,
        limit: this.provisionalLimit
      });
    } else if (departmentId == ApplicantDepartmentId) {
      response = await new ApplicantApi().getApplicants({
        status: this.applicantStatus,
        limit: this.provisionalLimit,
        analysisFlg: this.analysisFlg
      });
    } else if (departmentId === RetiredDepartmentId) {
      response = await new EmployeeApi().getRetired({
        limit: this.provisionalLimit,
        analysisFlg: this.analysisFlg
      });
    } else {
      response = await new DepartmentApi().getEmployees({
        departmentId,
        limit: this.provisionalLimit
      });
    }
    response = response.data.lists!;
    if (!response) {
      this.isReading = false;
      return;
    }
    this.departmentUsers[departmentId] = response.map((user: GetApplicantProfile | GetEmployeeProfile | GetRetiredProfile) => {
      let id = "";
      if (departmentId == ApplicantDepartmentId || this.applicantOnly) {
        id = `APL#${user.id}#`;
      } else if (departmentId == RetiredDepartmentId && "SK" in user) {
        id = user.SK;
      } else {
        id = `EMP#${user.id}#`;
      }
      return Object.assign({ department: department }, user, {
        id,
        userId: id,
        selected: this.selectedUsers.some(su => su.id === id),
        disabled: this.selectedUsers.some(su => su.id === id && su.disabled)
      });
    });
  }
  /**
   * 選択した従業員・応募者のチェック状態をdepartmentUsersおよびoccupationUsersに反映する
   * this.departmentUsersとthis.occupationUsersにチェックボックスの状態を反映する
   * @param {SelectableProfile | SelectableProfile[]} users 選択した従業員・応募者
   * @return void
   */
  private syncUserSelection(users: SelectableProfile | SelectableProfile[]) {
    type userCond = { id: string; selected: boolean; disabled: boolean }[];
    let userCond: userCond;
    if (users && Array.isArray(users)) {
      userCond = users.map(user => ({ id: user.id, selected: user.selected!, disabled: user.disabled! }));
    } else {
      userCond = [{ id: users.id, selected: users.selected!, disabled: users.disabled! }];
    }
    Object.values(this.departmentUsers).forEach(depGroup => {
      const matched = depGroup.filter(u => userCond.map(u => u.id).includes(u.id));
      if (matched) {
        depGroup.forEach(u => {
          const a = userCond.find(uu => uu.id === u.id);
          if (a) {
            u.selected = a.selected;
            u.disabled = a.disabled;
          }
        });
      }
    });
    Object.values(this.occupationUsers).forEach(occGroup => {
      const matched = occGroup.filter(u => userCond.map(u => u.id).includes(u.id));
      if (matched) {
        occGroup.forEach(u => {
          const a = userCond.find(uu => uu.id === u.id);
          if (a) {
            u.selected = a.selected;
            u.disabled = a.disabled;
          }
        });
      }
    });
  }
  /**
   * 選択した従業員・応募者のチェック状態をdepartmentUsersおよびoccupationUsersに反映する
   * this.departmentUsersとthis.occupationUsersにチェックボックスの状態を反映する
   * @param {SelectableProfile | SelectableProfile[]} selectedUser
   * @return {SelectableProfile[]} this.selectedUsers 選択された従業員・応募者
   */
  @Emit("user-selection-change")
  onUserSelect(selectedUser: SelectableProfile) {
    if (!this.currentDepartment && !this.currentOccupation) return;
    this.syncUserSelection(selectedUser);
    const filtered1 = Object.values<SelectableProfile[]>(this.departmentUsers)
      .map(users => users.filter(user => user.selected))
      .reduce((a, b) => a.concat(b), []);
    const filtered2 = Object.values<SelectableProfile[]>(this.occupationUsers)
      .map(users => users.filter(user => user.selected))
      .reduce((a, b) => a.concat(b), []);
    const ids: string[] = [];
    this.selectedUsers = this.selectedUsers
      .filter(user => user.id !== selectedUser.id || selectedUser.selected)
      .concat(filtered1.concat(filtered2))
      .filter(u => {
        if (ids.includes(u.id)) return false;
        ids.push(u.id);
        return true;
      });
    return this.selectedUsers;
  }
  /**
   * 部署か職種のカテゴリから結果をセットする
   * @param {string} category
   * @return void
   */
  async onCategorySelect(category: string) {
    this.isReading = false;
    this.currentCategory = category;
    if (this.currentCategory === "occupation" && !this.occupations.length) {
      const response = await new SystemApi().getOccupations({
        usedWithinClient: true,
        participantType: this.applicantOnly ? "applicant" : "employee"
      });
      this.occupations = response.data;
    } else if (this.currentCategory === "department" && !this.departments.length) {
      this.departments = Store.getInstance().getDepartments({});
    }
  }
  /**
   * 職種idから従業員・応募者の一覧をセットする
   * @param {Occupation} occupation 職種オブジェクト
   * @return void
   */
  async onOccupationSelect(occupation: Occupation) {
    this.isReading = true;
    this.currentOccupation = occupation;
    const occupationId = occupation.id;
    if (this.occupationUsers[occupationId]) return;
    let response;
    if (this.applicantOnly) {
      const res = await new ApplicantApi().getApplicants({
        occupationId: occupationId,
        status: this.applicantStatus,
        limit: this.provisionalLimit
      });
      response = res.data.lists!;
    } else {
      const res = await new EmployeeApi().searchEmployees({
        occupationId: occupationId,
        limit: this.provisionalLimit
      });
      response = res.data.lists!;
    }
    if (response === null) {
      this.occupationUsers[occupationId] = [];
    } else {
      this.occupationUsers[occupationId] = response.map((user: GetApplicantProfile | GetEmployeeProfile) => {
        const id = this.applicantOnly ? `APL#${user.id}#` : `EMP#${user.id}#`;
        return Object.assign({ occupation: occupation }, user, {
          id,
          userId: id,
          selected: this.selectedUsers.some(su => su.id === id),
          disabled: this.selectedUsers.some(su => su.id === id && su.disabled)
        });
      });
    }
  }
  /**
   * 全選択ボタンアクション
   * 部署または職種を選択した状態でクリックした場合に、内包している従業員・応募者の選択状態変更を行う
   * @param None
   * @return {SelectableProfile[]} this.selectedUsers 選択された従業員・応募者
   */
  @Emit("user-selection-change")
  onUserAllSelect() {
    let onSelectedUsers: SelectableProfile[];
    if (this.currentCategory === "department" && this.currentDepartment) {
      onSelectedUsers = Object.values(this.departmentUsers[this.currentDepartment.id]).map(user =>
        Object.assign(user, (user.selected = true))
      );
    } else if (this.currentCategory === "occupation" && this.currentOccupation) {
      onSelectedUsers = Object.values(this.occupationUsers[this.currentOccupation.id]).map(user =>
        Object.assign(user, (user.selected = true))
      );
    } else {
      return;
    }
    const ids: string[] = [];
    this.selectedUsers = this.selectedUsers.concat(onSelectedUsers).filter((obj, index, self) => {
      let firstIndex = self.findIndex(x => obj.id === x.id);
      return firstIndex === index;
    });
    this.syncUserSelection(onSelectedUsers);
    return this.selectedUsers;
  }
  /**
   * 選択された従業員・応募者をすべてクリアする
   * disabledがtrueは除く
   * @param None
   * @return {SelectableProfile[]} this.selectedUsers disabledフラグがONのユーザー
   */
  @Emit("user-selection-change")
  onUserNoneSelect() {
    const unCheckUsers = this.selectedUsers
      .filter(user => !user.disabled)
      .map(user => Object.assign(user, (user.selected = false)));
    this.selectedUsers = this.selectedUsers.filter(user => user.disabled);
    this.syncUserSelection(unCheckUsers);
    return this.selectedUsers;
  }
  /**
   * 選択完了ボタンアクション
   * @param None
   * @return None
   */
  @Emit("on-selection-done-button-click")
  onSelectionDoneButtonClick() {
    this.processing = true;
  }
  @Watch("storeInstance.state.departments")
  stateDepartmentsChange() {
    this.loadInitialData();
  }
  get selectedUserCount(): number {
    return this.selectedUsers ? this.selectedUsers.length : 0;
  }
}
